"use client";

import { useState, useMemo, useCallback } from "react";
import * as Select from "@radix-ui/react-select";
import Link from "next/link";
import { ChevronDown, Check, Lock } from "lucide-react";
import { DIAGNOSTICS, DOMAIN_OPTIONS, type DiagnosticTemplate } from "@/modes/diagnostics/data";
import { DiagnosticIcon } from "./DiagnosticIcons";
import { AssessmentFlow } from "@/modes/diagnostics/components/AssessmentFlow";
import { AuthPromptModal } from "@/modes/draft/components/shared/AuthPromptModal";
import { useAuth } from "@/platform/auth/useAuth";
import { countUserDiagnostics } from "@/platform/firebase/firestore/diagnostics";
import { planToTier } from "@/shared/types";

function DiagnosticCard({
  d,
  onStart,
}: {
  d:       DiagnosticTemplate;
  onStart: (d: DiagnosticTemplate) => void;
}) {
  return (
    <article className={`diag-card d-${d.dom}`}>
      <div className="card-head">
        <div className="card-icon">
          <DiagnosticIcon iconKey={d.icon} size={18} />
        </div>
        <span className={`card-tag d-${d.dom}`}>{d.tag}</span>
      </div>
      <h3>{d.title}</h3>
      <p>{d.desc}</p>
      <div className="card-foot">
        <span className="card-time">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          ~10 min
        </span>
        <button
          className="card-cta"
          type="button"
          aria-label={`Start ${d.title}`}
          onClick={() => onStart(d)}
        >
          Start
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>
    </article>
  );
}

interface Props {
  /** When true, Start buttons open the live assessment flow. Default: true. */
  interactive?: boolean;
}

export function DiagnosticsGrid({ interactive = true }: Props) {
  const [search,   setSearch]   = useState("");
  const [domain,   setDomain]   = useState("all");
  const [selected, setSelected] = useState<DiagnosticTemplate | null>(null);
  const [showAuth, setShowAuth]  = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return DIAGNOSTICS.filter((d) => {
      const matchSearch = !q || (d.title + " " + d.desc).toLowerCase().includes(q);
      const matchDomain = domain === "all" || d.tag === domain;
      return matchSearch && matchDomain;
    });
  }, [search, domain]);

  const { user, profile, loading: authLoading } = useAuth();

  const handleStart = useCallback(async (d: DiagnosticTemplate) => {
    if (!interactive) return;
    if (!user) {
      setShowAuth(true);
      return;
    }
    if (authLoading || !profile) return;
    if (profile.systemRole === "superadmin") {
      // superadmins bypass all plan limits
    } else {
      const tier = planToTier(profile.subscription?.plan ?? "free");
      if (tier === "free") {
        const count = await countUserDiagnostics(user.uid);
        if (count >= 1) {
          setLimitReached(true);
          return;
        }
      }
    }
    setSelected(d);
  }, [interactive, user, profile, authLoading]);

  return (
    <>
      <div className="diag-page">
        {/* Toolbar */}
        <div className="toolbar" role="search">
          <div className="search-box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search diagnostics…"
              aria-label="Search diagnostics"
            />
          </div>

          <Select.Root value={domain} onValueChange={setDomain}>
            <Select.Trigger className="domain-select" aria-label="Filter by domain">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <Select.Value />
              <Select.Icon asChild>
                <ChevronDown width={14} height={14} className="chev" aria-hidden="true" />
              </Select.Icon>
            </Select.Trigger>
            <Select.Portal>
              <Select.Content className="select-dropdown" position="popper" sideOffset={6}>
                <Select.Viewport>
                  {DOMAIN_OPTIONS.map((opt) => (
                    <Select.Item key={opt.value} value={opt.value} className="select-item">
                      <Select.ItemIndicator><Check width={12} height={12} /></Select.ItemIndicator>
                      <Select.ItemText>{opt.label}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>

        {/* Results count */}
        <p className="results-count" aria-live="polite" aria-atomic="true">
          <strong>{filtered.length}</strong>{" "}
          {filtered.length === 1 ? "diagnostic" : "diagnostics"}
        </p>

        {/* Grid */}
        <div className="diag-grid" role="list">
          {filtered.map((d) => (
            <DiagnosticCard key={d.id} d={d} onStart={handleStart} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="diag-empty">
            <p>No diagnostics match your search.</p>
          </div>
        )}
      </div>

      {/* Auth modal */}
      {showAuth && (
        <AuthPromptModal onDismiss={() => setShowAuth(false)} />
      )}

      {/* Limit reached modal */}
      {limitReached && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setLimitReached(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm mx-4 text-center" onClick={(e) => e.stopPropagation()}>
            <Lock size={36} className="mx-auto mb-4 text-indigo-500" />
            <h3 className="text-lg font-bold text-gray-800 mb-2">Free Plan Limit Reached</h3>
            <p className="text-sm text-gray-600 mb-6">
              Free users can create 1 diagnostic. Upgrade to Pro for unlimited diagnostics.
            </p>
            <Link
              href="/pricing"
              className="inline-block px-6 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-[#5B6CFF] to-[#EC4899] hover:from-[#4B5CEF] hover:to-[#D94185] transition-all"
            >
              Upgrade to Pro
            </Link>
          </div>
        </div>
      )}

      {/* Assessment flow */}
      {selected && (
        <AssessmentFlow
          diagnostic={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
